/**
 * ConversationTree — the Claude.ai-style branching tree that backs the v2 chat
 * surface. A flat `turns: [...]` array is a degenerate case (linear path); a
 * branched conversation is a tree where editing a prompt or regenerating an
 * answer creates a sibling node, and the user navigates among siblings with
 * ◀ N/M ▶ arrows.
 *
 * Public shape (mirrors the on-the-wire schema persisted by the backend):
 *
 *   nodes:           Map<id, { id, role, content, parent, children, createdAt }>
 *   rootId:          id of the first node ever appended (the *primary* root;
 *                    see the sibling-root note below)
 *   cursorId:        id of the leaf currently displayed
 *   preferredChild:  Map<parentId, childId>  — remembers which sibling the
 *                    user last visited under each branching point, so that
 *                    `switchSibling` walking back through a parent doesn't
 *                    reset deeper choices.
 *
 * Node ids are short opaque strings — they don't carry any meaning. The
 * `turns` array consumed by the rest of the engine is derived from this tree
 * by walking root → cursor; see `getCurrentPath()`.
 *
 * Sibling roots (PLEX-4226): the tree is really a *forest* — any node with
 * `parent === null` is a root. A normal conversation has exactly one root, but
 * editing the very first prompt forks a sibling root so the original first
 * prompt stays navigable via ◀ N/M ▶ (Claude.ai-style) instead of being
 * silently overwritten. The forest is implicit in the parent pointers, so the
 * single-valued `rootId` wire field stays backward-compatible: it just pins the
 * *first* root, and `getSiblings`/`switchSibling` treat all parentless nodes as
 * siblings of one another. `getCurrentPath()` walks up from `cursorId`, so it
 * never needed `rootId` and works unchanged across multiple roots.
 */

const newId = () => {
  // 12 chars of base36 from crypto.getRandomValues — collision-safe inside a
  // single conversation tree without pulling in a uuid dep.
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map((b) => b.toString(36).padStart(2, "0")).join("");
  }
  return Math.random().toString(36).slice(2, 14);
};

const nowISO = () => new Date().toISOString();

export class ConversationTree {
  constructor() {
    this.nodes = new Map();
    this.rootId = null;
    this.cursorId = null;
    this.preferredChild = new Map();
  }

  size() {
    return this.nodes.size;
  }

  // Append a new node as the child of the current cursor (or as root if the
  // tree is empty). Returns the new node.
  append({ role, content }) {
    const id = newId();
    const node = {
      id,
      role,
      content,
      parent: this.cursorId,
      children: [],
      createdAt: nowISO(),
    };
    this.nodes.set(id, node);
    if (this.cursorId) {
      const parent = this.nodes.get(this.cursorId);
      parent.children.push(id);
      this.preferredChild.set(this.cursorId, id);
    } else {
      this.rootId = id;
    }
    this.cursorId = id;
    return node;
  }

  // Create a new sibling under the same parent as `siblingId` and move the
  // cursor onto the new node. Used when the user edits a prompt or
  // regenerates an answer — the original sibling stays in the tree so the
  // user can navigate back to it via ◀.
  //
  // PLEX-4226: `siblingId` may be a root (parent === null). Branching a root
  // forks a *sibling root* — another parentless node — rather than throwing.
  // This is what makes editing the very first prompt create a navigable branch
  // (◀ 1/2 ▶ between the original and edited first prompt) instead of the old
  // silent-overwrite footgun. The original root's whole subtree is untouched
  // and reachable via `switchSibling`; nothing is orphaned.
  branchSibling(siblingId, { role, content }) {
    const sibling = this.nodes.get(siblingId);
    if (!sibling) throw new Error(`branchSibling: unknown node ${siblingId}`);

    const id = newId();
    const node = {
      id,
      role,
      content,
      parent: sibling.parent, // null when branching a root → new sibling root
      children: [],
      createdAt: nowISO(),
    };
    this.nodes.set(id, node);
    if (sibling.parent) {
      // Ordinary sibling: attach under the shared parent and remember it as
      // that parent's preferred child so a later walk-down lands here.
      this.nodes.get(sibling.parent).children.push(id);
      this.preferredChild.set(sibling.parent, id);
    }
    // else: sibling root — the forest of roots is implicit in the parent
    // pointers (see getSiblings / _roots). `rootId` stays pinned to the first
    // root for wire compatibility; the new root is discovered by parent===null.
    this.cursorId = id;
    return node;
  }

  // All parentless nodes (the roots of the forest), in insertion order. A
  // normal conversation has exactly one; a root-prompt edit (PLEX-4226) forks
  // additional sibling roots. Insertion order mirrors how `children` arrays
  // already encode sibling order, so ◀ N/M ▶ indexing is stable.
  _roots() {
    const roots = [];
    for (const node of this.nodes.values()) {
      if (!node.parent) roots.push(node);
    }
    return roots;
  }

  // Walk root → cursor and return the nodes along the way, in order. The v2
  // engine's `turns` array is exactly this list (sans tree-only fields).
  getCurrentPath() {
    if (!this.cursorId) return [];
    const reversed = [];
    let id = this.cursorId;
    const visited = new Set();
    while (id) {
      if (visited.has(id)) break; // cycle guard
      visited.add(id);
      const node = this.nodes.get(id);
      if (!node) break;
      reversed.push(node);
      id = node.parent;
    }
    return reversed.reverse();
  }

  // Returns { siblings: [...nodes], index, total } for the BranchNav UI.
  // For a node with no parent (root) or no co-children, total is 1 and the
  // navigator should not render.
  getSiblings(nodeId) {
    const node = this.nodes.get(nodeId);
    if (!node) return { siblings: [], index: 0, total: 0 };
    if (!node.parent) {
      // PLEX-4226: parentless nodes are siblings of one another. For a normal
      // single-root conversation this is a one-element list — total === 1, so
      // the UI still suppresses BranchNav on the first turn (unchanged). After
      // a root-prompt edit there are ≥2 roots, so ◀ N/M ▶ appears there too.
      const roots = this._roots();
      const index = roots.findIndex((n) => n.id === nodeId);
      return { siblings: roots, index: index < 0 ? 0 : index, total: roots.length };
    }
    const parent = this.nodes.get(node.parent);
    if (!parent) return { siblings: [node], index: 0, total: 1 };
    const siblings = parent.children.map((id) => this.nodes.get(id)).filter(Boolean);
    const index = siblings.findIndex((n) => n.id === nodeId);
    return { siblings, index: index < 0 ? 0 : index, total: siblings.length };
  }

  // Move cursor to a different sibling of `currentNodeId` (direction = +1 or -1)
  // and walk down via `preferredChild` until a leaf is reached. Returns the
  // new leaf node, or null if there are no siblings.
  switchSibling(currentNodeId, direction) {
    const { siblings, index, total } = this.getSiblings(currentNodeId);
    if (total <= 1) return null;
    const nextIdx = ((index + direction) % total + total) % total;
    const next = siblings[nextIdx];
    if (!next) return null;
    if (next.parent) this.preferredChild.set(next.parent, next.id);

    // Walk down to a leaf, preferring the user's last-visited child at each
    // branching point.
    let leaf = next;
    const guard = new Set();
    while (leaf.children.length > 0) {
      if (guard.has(leaf.id)) break;
      guard.add(leaf.id);
      const preferred = this.preferredChild.get(leaf.id);
      const childId = preferred && leaf.children.includes(preferred)
        ? preferred
        : leaf.children[leaf.children.length - 1]; // newest by default
      const child = this.nodes.get(childId);
      if (!child) break;
      leaf = child;
    }
    this.cursorId = leaf.id;
    return leaf;
  }

  // PLEX-6383: give node `oldId` the id `newId`, everywhere the tree refers to it.
  // When the backend writes the conversation, it keys each node by the id of the
  // SSE "message" event that published the turn (rd history/tree.py); the engine
  // created the node at message_start with a local id and takes the backend's when
  // that event arrives. Returns false (and changes nothing) when `oldId` is unknown
  // or `newId` is taken.
  rekey(oldId, newId) {
    if (!newId || oldId === newId || !this.nodes.has(oldId) || this.nodes.has(newId)) return false;
    const node = this.nodes.get(oldId);
    this.nodes.delete(oldId);
    node.id = newId;
    this.nodes.set(newId, node);
    const parent = node.parent ? this.nodes.get(node.parent) : null;
    if (parent) parent.children = parent.children.map((cid) => (cid === oldId ? newId : cid));
    for (const cid of node.children) {
      const child = this.nodes.get(cid);
      if (child) child.parent = newId;
    }
    if (this.preferredChild.has(oldId)) {
      this.preferredChild.set(newId, this.preferredChild.get(oldId));
      this.preferredChild.delete(oldId);
    }
    for (const [k, v] of this.preferredChild) {
      if (v === oldId) this.preferredChild.set(k, newId);
    }
    if (this.cursorId === oldId) this.cursorId = newId;
    if (this.rootId === oldId) this.rootId = newId;
    return true;
  }

  // Truncate the cursor's path at `nodeId` — everything below stays in the
  // tree, just the cursor moves up. Used by `branchEdit` when the engine
  // needs to start fresh from a chosen node before sending the prompt.
  setCursor(nodeId) {
    if (!this.nodes.has(nodeId)) throw new Error(`setCursor: unknown node ${nodeId}`);
    this.cursorId = nodeId;
  }

  // PLEX-4226: deterministic integrity pass for a hydrated tree. A persisted
  // record can be internally inconsistent — a cursorId/rootId/preferredChild
  // pointing at a node that no longer exists, a one-directional parent↔child
  // link, or a parent chain that loops — because of a partial mid-stream save,
  // an aborted migration, a hand-edited DynamoDB item, or a bug in an older
  // writer. Left unrepaired, `getCurrentPath()` silently returns [] (the
  // conversation renders blank) or a downstream op throws.
  //
  // sanitize() repairs the tree in place and returns a list of human-readable
  // repair descriptions so the caller can log what it fixed. It never throws.
  // It preserves the user's last edit: when the cursor has to be rebuilt, the
  // fallback leaf-walk biases toward the newest branch at each fork (new nodes
  // are appended last and recorded in preferredChild), which is where an
  // edit/regenerate just moved the user.
  sanitize() {
    const repairs = [];

    // 1. children arrays: drop ids that don't resolve to a live node and any
    //    duplicates, so getSiblings/switchSibling never surface a phantom or
    //    double-counted sibling.
    for (const node of this.nodes.values()) {
      if (!Array.isArray(node.children)) {
        node.children = [];
        repairs.push(`node ${node.id}: children was not an array; reset to []`);
        continue;
      }
      const seen = new Set();
      const cleaned = node.children.filter((cid) => {
        if (seen.has(cid) || !this.nodes.has(cid)) return false;
        seen.add(cid);
        return true;
      });
      if (cleaned.length !== node.children.length) {
        repairs.push(
          `node ${node.id}: pruned ${node.children.length - cleaned.length} dangling/duplicate child id(s)`,
        );
        node.children = cleaned;
      }
    }

    // 2. parent pointers: a parent that no longer exists orphans the node into
    //    a root; a valid parent that doesn't list this node as a child is a
    //    broken one-directional link — re-link it so both navigation
    //    directions agree.
    for (const node of this.nodes.values()) {
      if (node.parent && !this.nodes.has(node.parent)) {
        repairs.push(`node ${node.id}: parent ${node.parent} missing; promoted to root`);
        node.parent = null;
      }
      // Break a parent cycle BEFORE the re-link below. If two nodes' parent
      // pointers already point at each other, re-linking "the missing
      // direction" pushes each into the other's `children` and turns a
      // parent-only cycle into a genuine bidirectional one (PR #31 review).
      // _pathHasCycle only walks `.parent`, so step 4 would move the cursor
      // away from it but leave the structure cyclic for any downward walker.
      // Orphaning the node is the conservative repair: it cannot invent a
      // false relationship, and step 3/4 then re-root and re-seat the cursor.
      if (node.parent && this._pathHasCycle(node.id)) {
        repairs.push(`node ${node.id}: parent chain cycles via ${node.parent}; promoted to root`);
        node.parent = null;
      }
      if (node.parent) {
        const parent = this.nodes.get(node.parent);
        if (!parent.children.includes(node.id)) {
          parent.children.push(node.id);
          repairs.push(`node ${node.id}: re-linked into parent ${node.parent}.children`);
        }
      }
    }

    // 2b. With parent cycles broken, drop any `children` entry that disagrees
    //     with the child's own `parent`. Step 1 only pruned dangling/duplicate
    //     ids, so a stale reciprocal entry written by an earlier corrupt state
    //     can still survive and cycle a downward walk.
    for (const node of this.nodes.values()) {
      const kept = node.children.filter((cid) => {
        const child = this.nodes.get(cid);
        return child && child.parent === node.id;
      });
      if (kept.length !== node.children.length) {
        repairs.push(
          `node ${node.id}: dropped ${node.children.length - kept.length} child id(s) whose parent disagrees`,
        );
        node.children = kept;
      }
    }

    const roots = this._roots();

    // 3. rootId: must reference a live parentless node; fall back to the first
    //    root actually present (or null for an empty tree).
    if (this.rootId && !this.nodes.has(this.rootId)) {
      const fallback = roots.length ? roots[0].id : null;
      repairs.push(`rootId ${this.rootId} missing; reset to ${fallback}`);
      this.rootId = fallback;
    } else if (!this.rootId && roots.length) {
      this.rootId = roots[0].id;
      repairs.push(`rootId was null; set to ${this.rootId}`);
    }

    // 4. cursorId: must reference a live node AND its root→cursor walk must
    //    terminate. Fall back to a deterministic leaf (deepest node under the
    //    primary root, preferring the last-visited/newest branch at each fork).
    if (this.cursorId && !this.nodes.has(this.cursorId)) {
      const fallback = this._deterministicLeaf(roots);
      repairs.push(`cursorId ${this.cursorId} missing; reset to ${fallback}`);
      this.cursorId = fallback;
    } else if (this.cursorId && this._pathHasCycle(this.cursorId)) {
      const fallback = this._deterministicLeaf(roots);
      repairs.push(`cursorId ${this.cursorId} path contains a cycle; reset to ${fallback}`);
      this.cursorId = fallback;
    } else if (!this.cursorId && roots.length) {
      this.cursorId = this._deterministicLeaf(roots);
      repairs.push(`cursorId was null; set to ${this.cursorId}`);
    }

    // 5. preferredChild: every key must be a live node and every value must be
    //    an actual child of that key. Drop stale hints so switchSibling's
    //    walk-down never chases a pruned node.
    for (const [parentId, childId] of Array.from(this.preferredChild.entries())) {
      const parent = this.nodes.get(parentId);
      if (!parent || !parent.children.includes(childId)) {
        this.preferredChild.delete(parentId);
        repairs.push(`preferredChild[${parentId}] → ${childId} stale; dropped`);
      }
    }

    return repairs;
  }

  // Does the root→node parent walk from `startId` loop back on itself? A cycle
  // would make getCurrentPath's own guard bail early and truncate the path.
  _pathHasCycle(startId) {
    const visited = new Set();
    let id = startId;
    while (id) {
      if (visited.has(id)) return true;
      visited.add(id);
      const node = this.nodes.get(id);
      if (!node) return false; // dangling parent, not a cycle
      id = node.parent;
    }
    return false;
  }

  // Deepest leaf reachable from the primary root, following preferredChild at
  // each branch (else the newest child). Deterministic given the tree + prefs;
  // used as the cursor fallback in sanitize().
  _deterministicLeaf(roots) {
    let node = (this.rootId && this.nodes.get(this.rootId))
      || (roots && roots.length ? roots[0] : null);
    if (!node) return null;
    const guard = new Set();
    while (node && Array.isArray(node.children) && node.children.length > 0) {
      if (guard.has(node.id)) break;
      guard.add(node.id);
      const preferred = this.preferredChild.get(node.id);
      const childId = preferred && node.children.includes(preferred)
        ? preferred
        : node.children[node.children.length - 1]; // newest by default
      const child = this.nodes.get(childId);
      if (!child) break;
      node = child;
    }
    return node ? node.id : null;
  }

  // Build a linear tree from a flat turns array (lazy migration of legacy
  // records). Each turn becomes a node in a parent→child chain; cursor lands
  // on the last node.
  static fromTurns(turns) {
    const tree = new ConversationTree();
    for (const t of turns || []) {
      tree.append({ role: t.role, content: t.content });
    }
    return tree;
  }

  // Wire-shape <- serialized form
  static fromJSON({ nodes, rootId, cursorId, preferredChild } = {}) {
    const tree = new ConversationTree();
    if (nodes && typeof nodes === "object") {
      for (const [id, raw] of Object.entries(nodes)) {
        tree.nodes.set(id, {
          id,
          role: raw.role,
          content: raw.content,
          parent: raw.parent || null,
          children: Array.isArray(raw.children) ? [...raw.children] : [],
          createdAt: raw.createdAt || null,
        });
      }
    }
    tree.rootId = rootId || null;
    tree.cursorId = cursorId || null;
    if (preferredChild && typeof preferredChild === "object") {
      for (const [k, v] of Object.entries(preferredChild)) tree.preferredChild.set(k, v);
    }
    return tree;
  }

  // Wire-shape -> serialized form. Designed to match what the backend's
  // serialize_conversation expects (see plex/search/history/handlers.py).
  toJSON() {
    const nodesObj = {};
    for (const [id, node] of this.nodes) {
      nodesObj[id] = {
        role: node.role,
        content: node.content,
        parent: node.parent,
        children: [...node.children],
        createdAt: node.createdAt,
      };
    }
    return {
      nodes: nodesObj,
      rootId: this.rootId,
      cursorId: this.cursorId,
      preferredChild: Object.fromEntries(this.preferredChild),
    };
  }
}

export default ConversationTree;
